import { lazy, Suspense, useEffect, useState } from "react";
import useStore from "../../store/useStore";
import useShots from "../../hooks/useShots";
import usePlanDetails from "../../hooks/usePlanDetails";
import Header from "./components/Header";
import { Button, Modal } from "antd";
import { getDecodedToken, getLocalStorage, setLocalStorage } from "../../utils/localStorage";
import { COUNTRY_ID_KEY } from "../../utils/constants";
import { getCountryIdFromMobile } from "../../utils/helper";
import { getBanners, getPediaDoctorDetails, pediaActiveSessions } from "../account/service";
import { useNavigate } from "react-router-dom";
import "./../onBoarding/OnBoarding.css";
import alertCircle from "../../assets/images/alert-circle.svg";
import { getUserPreferences } from "../tatvaPedia/service";
import {
  fetchUserPreferences,
  fetchChatHistoryAi,
} from "../tatvaShots/service";



import DifferentialDiagnosisCard from "./components/DifferentialDiagnosisCard";
import { CloseOutlined } from "@ant-design/icons";
import { isZymedApp } from "../../utils/appConfig";
import { shouldShowProfileModal } from "../../utils/helper";
import useDeviceInfo from "../tatvaPedia/UseDeviceInfo";
import { useZymedHomeViewed } from "../../utils/zymedAnalytics";
import { trackDeviceLimitShown } from "../../utils/zymedSessionDiagnostics";

const PediaTrendingSection = lazy(
  () => import("../tatvaPedia/components/PediaTrendingSection")
);
// Lazy load sections for better performance
const TatvaShots = lazy(() => import("./components/TatvaShots"));
const TrendingSection = lazy(
  () => import("../tatvaShots/components/TrendingSection")
);
const TatvaAI = lazy(() => import("./components/TatvaAI"));
const RemoteCare = lazy(() => import("./components/RemoteCare"));
const Recommendation = lazy(() => import("./components/Recommendation"));
const ChatHistory = lazy(() => import("./components/ChatHistory"));
const Features = lazy(() => import("./components/Features"));
const ServicesSection = lazy(() => import("./components/ServicesSection"));

const HomePs = () => {
  useZymedHomeViewed();
  const { planDetails } = useStore();
  const { getShots } = useShots();
  const { getPlanDetails, planLoader } = usePlanDetails();
  const navigate = useNavigate();
  const tokenData: any = getDecodedToken();
  const doctorDetails: any = tokenData?.user || {};

  const [showProfileModal, setShowProfileModal] = useState(false);

  const [banners, setBanners]: any = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [hasPediaPreferences, setHasPediaPreferences] = useState(false);
  const [hasShotsPreferences, setHasShotsPreferences] = useState(false);
  const [hasChats, setHasChats] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { setPediaUserPreferences } = useStore();
  const device: any = useDeviceInfo();

  const [deviceInfo, setDeviceInfo] = useState(device);
  // Handle modal close with timestamp storage
  const handleModalClose = () => {
    localStorage.setItem("profileModalLastClosed", Date.now().toString());
    setShowProfileModal(false);
  };

  const getFeatures = async () => {
    const tokenData: any = getDecodedToken();
    const bannerData: any = await getBanners(tokenData?.user?.b2cId);

    if (bannerData?.status === 200) {
      const bannersList = bannerData?.data?.body || [];
      
      // Set banners first for immediate state update
      setBanners(bannersList);
      
      // Then aggressively preload banner images in the background
      if (bannersList.length > 0) {
        const bannerUrls = bannersList
          .map((banner: any) => banner?.banner)
          .filter(Boolean);
        
        if (bannerUrls.length > 0) {
          // Preload ONLY the first slide. The banner artwork served by the API is very
          // heavy (~10-20MB per SVG, ~66MB for the full set), so preloading every URL
          // pulled the whole carousel down before the page could settle - and it defeated
          // the `loading="lazy"` the carousel already applies to slides 2..n.
          // The remaining slides now load on demand as the carousel advances.
          const preloadBanners = () => {
            const [firstUrl] = bannerUrls;
            if (!firstUrl) return;
            const img = new Image();
            if ('fetchPriority' in img) {
              (img as any).fetchPriority = 'high';
            }
            img.src = firstUrl;
          };

          if ('requestIdleCallback' in window) {
            requestIdleCallback(preloadBanners, { timeout: 100 });
          } else {
            setTimeout(preloadBanners, 0);
          }
        }
      }
    }
  };

  const getDoctorDetails = async () => {
    if (doctorDetails?.mobileNumber) {
      const res: any = await getPediaDoctorDetails(doctorDetails?.mobileNumber);
      if (res?.status === 200) {
        localStorage.setItem("b2c_id", res?.data?.body?.b2c_id);
        localStorage.setItem("quiz_allowed", res?.data?.body?.quiz_allowed);
        localStorage.setItem("service_payer_logo", res?.data?.body?.ai_service_payer_logo);
        localStorage.setItem("service_payer_logo_white", res?.data?.body?.ai_service_payer_logo_white);
        if (res?.data?.body?.pm_id) {
          localStorage.setItem("pm_id", res?.data?.body?.pm_id);
        } else {
          localStorage.removeItem("pm_id");
        }
        const firstName = res?.data?.body?.first_name || "";
        const lastName = res?.data?.body?.last_name || "";
        const doctorName = `${firstName} ${lastName}`.trim();
        if (doctorName) {
          localStorage.setItem("doctor_name", doctorName);
        } else {
          localStorage.removeItem("doctor_name");
        }
        console.log('res?.data?.body', res?.data?.body)
        if (res?.data?.body?.speciality?.displayName) {
          localStorage.setItem("speciality_master_id", res?.data?.body?.speciality?.pmMasterId);
        } else {
          localStorage.removeItem("speciality_master_id");
        }
        // Only show modal if server says to show it AND 48 hours have passed since last close
        const serverSaysShow = res?.data?.body?.show_city_popup;
        setShowProfileModal(serverSaysShow && shouldShowProfileModal());

        setPromoCode(res?.data?.body?.access_code);

        // call active sessions api if access code startswith ZY
        if (res?.data?.body?.access_code.startsWith("ZY")) {
          const activeResponse: any = await pediaActiveSessions(res?.data?.body?.b2c_id);
          if (activeResponse !== undefined) {
            const sessions: any[] = activeResponse?.data?.body?.content;
            const deviceType = localStorage.getItem("device_type") + "_" + res?.data?.body?.b2c_id;

            let countSessions = sessions.filter((session: any) => session.deviceType !== deviceType).length;
            const allowedCount = res?.data?.body?.allowedCount || 1;
            if (countSessions >= allowedCount) {
              // navigate to session exceed screen
              // Check if the current deviceType is present in the first `allowedCount` sessions
              const isCurrentDeviceAllowed = sessions
                .slice(0, allowedCount)
                .some((session) => session?.deviceType === deviceType);

              // If not allowed, navigate to session exceed page
              if (!isCurrentDeviceAllowed) {
                trackDeviceLimitShown({ allowedCount, sessions, currentDeviceType: deviceType });
                navigate(`/session-exceed?b2c=${res?.data?.body?.b2c_id}&allowedCount=${allowedCount}`);
              }
            }
          }
        }
        const cid = getCountryIdFromMobile(res?.data?.body?.mobile_number || doctorDetails?.mobileNumber);
        if (cid != null) setLocalStorage(COUNTRY_ID_KEY, cid);
      }
    }
  };

  // Fetch plan details and shots on component mount
  useEffect(() => {

    const fetchData = async () => {
      /*
       * Split the boot sequence into a critical path and background work.
       *
       * The page-level loader below is gated on isInitialLoading, so previously the whole
       * dashboard waited on the SLOWEST of six calls - chat-history (~950ms) and the
       * shots feed (~1.25s) among them - even though each of those only feeds a section
       * that already renders its own skeleton. Everything now paints as soon as the plan
       * and doctor details land; the rest fills in behind it.
       *
       * Kept on the critical path deliberately:
       *   getPlanDetails  - decides WHICH sections exist, so rendering before it lands
       *                     would flash the wrong layout.
       *   getDoctorDetails - can redirect to /session-exceed, and rendering the dashboard
       *                     first would flash it before the redirect.
       */
      const background = [
        getFeatures(),
        getShots("myFeed"),
        (async () => {
          // These three are independent - they were awaited one after another, which
          // serialised ~1.4s of latency for no reason.
          const [prefsData, shotsPrefs, chatHistory] = await Promise.all([
            getUserPreferences(tokenData?.user?.b2cId),
            fetchUserPreferences(),
            fetchChatHistoryAi(),
          ]);

          if (prefsData?.status === 200) {
            setPediaUserPreferences(prefsData?.body);
            setHasPediaPreferences(!!prefsData?.body?.specialities);
          }
          setHasShotsPreferences(!!shotsPrefs);
          setHasChats(!!chatHistory?.length);
        })(),
      ];
      background.forEach((p) =>
        p?.catch?.((error: unknown) =>
          console.error("Error fetching background dashboard data:", error)
        )
      );

      try {
        await Promise.all([getPlanDetails(), getDoctorDetails()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchData();
  }, []);

  const sections = [
    {
      // Show Pedia section for everyone
      condition: true,
      content: (
        <>
          <PediaTrendingSection />
        </>
      ),
    },
    {
      condition: !!planDetails?.shot_plan,
      content: (
        <>
          {hasShotsPreferences ? (
            <TrendingSection isHomePage />
          ) : (
            <TatvaShots />
          )}
        </>
      ),
    },
    {
      condition: !!planDetails?.ai_plan,
      content: <>{hasChats ? <ChatHistory /> : <TatvaAI />}</>,
    },
    {
      condition: !!planDetails?.ddx_plan,
      content: (
        <>
          <DifferentialDiagnosisCard />
        </>
      ),
    },
    {
      condition: !!planDetails?.remote_plan && !isZymedApp(),
      content: (
        <>
          <RemoteCare />
          <Recommendation />
        </>
      ),
    },
  ];

  // Show single page-level loader while everything initializes
  if (isInitialLoading || planLoader) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9FF]">
        <div className="text-center space-y-6">
          <div className="space-y-2">
            <div className="text-blue-600 text-xl font-semibold tracking-wide">
              Loading Dashboard
            </div>
            <div className="text-gray-500 text-sm">
              Preparing your personalized medical content...
            </div>
          </div>
          <div className="flex justify-center space-x-2 mt-4">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      {/* Remove individual spinners - content shows only when ready */}
      <Suspense fallback={<></>}>
        <ServicesSection planDetails={planDetails} />
      </Suspense>
      <Suspense fallback={<></>}>
        <Features planDetails={planDetails} banners={banners} />
      </Suspense>

      <div className="flex flex-col gap-[25px] bg-[#F9F9FF]">
        <Suspense fallback={<></>}>
          {sections.map(
            (section, index) =>
              section.condition && (
                <div className="flex flex-col gap-[32px]" key={index}>
                  {section.content}
                </div>
              )
          )}
        </Suspense>
        <div className="mt-20"></div>
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              {" "}
              <img height={30} width={30} src={alertCircle} alt="" />
              &nbsp; Complete Your Profile{" "}
            </div>
          }
          style={{ top: 20 }}
          width={"353px"}
          height={"167px"}
          open={showProfileModal}
          className="profile-modal"
          closable={true}
          onCancel={handleModalClose}
          closeIcon={
            <span style={{ color: "white" }}>
              <CloseOutlined />
            </span>
          }
          footer={[
            <Button
              key={"add-details-btn"}
              onClick={() => navigate("/geo")}
              className="w-full bg-[#ED8A00] text-white border border-[#ED8A00]"
            >
              Add Details
            </Button>,
          ]}
        >
          <p className="ml-7">
            Add details like your city and qualifications to continue using the
            app seamlessly.
          </p>
        </Modal>
      </div>
    </>
  );
};

export default HomePs;
